import { makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import * as d3 from 'd3';
import React from 'react';
import { theme } from '../../../theme/theme';

const useBarChartCardClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: 20,
        gap: 50,
        borderRadius: 5,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        background: theme.colors.white,
        '& > span': {
            color: theme.colors.darkBlue[1],
            ...theme.typography.headings.h6,
            marginBottom: 0,
            textTransform: 'uppercase',
        },
    },
    stats: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 5,
    },
    stat: {
        display: 'flex',
        flex: 1,
        gap: 10,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        '& > div:first-child': {
            width: 2,
            minWidth: 2,
            borderRadius: 1,
        },
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        '& > span:first-child': {
            color: theme.colors.neutral[3],
            ...theme.typography.content.caption.paragraph,
        },
        '& > span:last-child': {
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: theme.colors.neutral[1],
            ...theme.typography.content.caption.paragraph,
            '& > span:first-child': {
                ...theme.typography.displays.d2,
            },
            '& > span:last-child': {
                ...theme.typography.content.label.default,
            },
        },
    },
    chart: {
        minHeight: 100,
        flex: 1,
    },
});

/**
 * Display a bar chart containing data information.
 * All data groups must have the same value unit.
 *
 * @param props.fetching show fetching skeletons
 * @param props.name card name
 * @param props.data data groups to display, name is the header, main is the center text and color is a list of one or
 *                   two strings to compute a linear gradient
 * @param props.footer extra component to be rendered at the bottom of the card
 */
export const BarChartCard = (props: {
    fetching?: boolean;
    name: string;
    data: { color: [string, string?]; name: string; main: string; value: number }[];
    footer?: React.ReactNode;
}) => {
    const classes = useBarChartCardClasses();

    if (props.fetching)
        return (
            <div className={classes.root}>
                <Skeleton />
                <div className={classes.stats}>
                    <Skeleton className={classes.stat} variant='rect' height={65} />
                    <Skeleton className={classes.stat} variant='rect' height={65} />
                </div>
                <Skeleton className={classes.chart} variant='rect' />
                {props.footer && <Skeleton variant='rect' />}
            </div>
        );

    return (
        <div className={classes.root}>
            <span>{props.name}</span>
            <div className={classes.stats}>
                {props.data.map((stat, i) => {
                    const background = `linear-gradient(${stat.color[0]}, ${stat.color[1 % stat.color.length]})`;
                    return (
                        <div key={i} className={classes.stat}>
                            <div style={{ background }} />
                            <div className={classes.labels}>
                                <span>{stat.name}</span>
                                <span>
                                    <span>{`${stat.main} `.slice(0, stat.main.indexOf(' '))}</span>
                                    <span>{`${stat.main} `.slice(stat.main.indexOf(' '))}</span>
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
            <BarChart className={classes.chart} data={props.data} />
            {props.footer}
        </div>
    );
};

/**
 * Simple bar chart widget built with d3.
 *
 * @param props.data data to be rendered, each index must contain the data point color and value
 */
const BarChart = (props: JSX.IntrinsicElements['div'] & { data: { color: [string, string?]; value: number }[] }) => {
    const { data, ...rootProps } = props;
    const root$ = React.useRef<SVGSVGElement>();

    React.useLayoutEffect(() => {
        if (!root$.current) return;
        const svg$ = root$.current;
        render(svg$, props.data);
        let size = { x: svg$.clientWidth, y: svg$.clientHeight };
        const interval = setInterval(() => {
            if (svg$.clientWidth === size.x && svg$.clientHeight === size.y) return;
            size = { x: svg$.clientWidth, y: svg$.clientHeight };
            render(svg$, props.data);
        }, 500);
        return () => clearInterval(interval);
    }, [root$.current, data]);

    const render = (svg$: SVGSVGElement, data: { color: [string, string?]; value: number }[]) => {
        const size = { x: svg$.clientWidth, y: svg$.clientHeight };
        const max = Math.max(10, ...data.map(data => data.value));
        const svg = d3.select(svg$).attr('viewBox', `0 0 ${size.x} ${size.y}`);

        // axis setup
        const labelSize = max.toString().length * 8;
        const xScale = d3
            .scaleBand([labelSize, size.x])
            .domain(data.map((_, i) => i.toString()))
            .paddingInner(0.3)
            .paddingOuter(0.4);
        const yScale = d3
            .scaleLinear()
            .domain([0, max])
            .range([size.y - 5, 0 + 5]);
        const yAxis = d3
            .axisLeft(yScale)
            .ticks(5)
            .tickSize(-size.x + labelSize);
        svg.selectAll('g')
            .data(['y-axis'])
            .join('g')
            .style('color', theme.colors.neutral[8])
            .attr('transform', `translate(${labelSize}, 0)`)
            .call(g => yAxis(g as any))
            .call(g => g.select('.domain').remove())
            .selectAll('text')
            .style('color', theme.colors.neutral[10]);

        // gradient setup
        svg.selectAll('defs')
            .data([undefined])
            .join('defs')
            .selectAll('linearGradient')
            .data(data)
            .join('linearGradient')
            .attr('id', (_, i) => `lg-${i}`)
            .attr('gradientTransform', 'rotate(90)')
            .selectAll('stop')
            .data(d => [d.color[0], d.color[1 % d.color.length]] as string[])
            .join('stop')
            .attr('stop-color', c => c)
            .attr('offset', (_, i) => `${i * 100}%`);

        // data bars
        svg.selectAll('rect')
            .data(data)
            .join('rect')
            .style('fill', (_, i) => `url(#lg-${i})`)
            .attr('x', (_, i) => xScale(i.toString())!)
            .attr('y', d => yScale(d.value))
            .attr('width', xScale.step() * (1 - xScale.padding()))
            .attr('height', d => yScale(0) - yScale(d.value))
            .attr('rx', 4);
    };

    return (
        <div {...rootProps} style={{ ...rootProps.style, position: 'relative', padding: 0, margin: 0 }}>
            <svg ref={root$ as any} style={{ position: 'absolute', width: '100%', height: '100%' }} />
        </div>
    );
};
